import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Asset, LogbookEntry, MaintenanceSummary, Task, Vendor, WorkOrder } from '@syndiqia/maintenance/contracts';
import { useApi } from './api';
import { useSyndicate } from './session';

function useMaintenanceQuery<T>(key: unknown[], path: string, enabled = true) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'maintenance', ...key],
    queryFn: () => api<T>(`${s.api}/maintenance${path}`),
    enabled: enabled && s.can('maintenance.read'),
  });
}

export const useWorkOrders = (status: 'open' | 'all', requestId?: string) =>
  useMaintenanceQuery<WorkOrder[]>(['work-orders', status, requestId ?? ''], `/work-orders?status=${status}${requestId ? `&requestId=${requestId}` : ''}`);
export const useWorkOrder = (id: string) => useMaintenanceQuery<WorkOrder>(['work-order', id], `/work-orders/${id}`);
export const useAssets = () => useMaintenanceQuery<Asset[]>(['assets'], '/assets');
export const useVendors = () => useMaintenanceQuery<Vendor[]>(['vendors'], '/vendors');
export const useTasks = () => useMaintenanceQuery<Task[]>(['tasks'], '/tasks');
export const useLogbook = (assetId?: string) => useMaintenanceQuery<LogbookEntry[]>(['logbook', assetId ?? ''], `/logbook${assetId ? `?assetId=${assetId}` : ''}`);
export const useMaintenanceSummary = () => useMaintenanceQuery<MaintenanceSummary>(['summary'], '/summary');

/** A write to the Maintenance API; refreshes everything maintenance (work orders, logbook, calendar, summary). */
export function useMaintenanceMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  const api = useApi();
  const s = useSyndicate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Input) => request(api, `${s.api}/maintenance`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'maintenance'] }),
  });
}
